/**
 * Her mother. The first character in the game other than Rosalie, and the whole
 * argument of beat 1.
 *
 * ## What this asset has to say, without a word
 *
 * Spec §7 beat 1: *"Her mother cannot turn around; the bars are the first thing
 * the player understands."* That sentence is a modelling brief, not a mood, and
 * it is satisfied by arithmetic rather than by staging:
 *
 *   - a farrowing crate's sow lane is **0.65 m wide** (`farrowing-crate.ts`),
 *   - this sow is **1.72 m long and 0.46 m across**,
 *   - so turning around needs 1.72 m of width in a space that has 0.65.
 *
 * The player is never told that. They see a body longer than its cage is wide.
 * Nothing about this object needs to be animated for it to land, and that is why
 * it is the first thing built.
 *
 * ## She is a plush toy, and she is not Rosalie
 *
 * Every animal in this world is a soft toy — spec §3 gives Holly wool "like torn
 * cotton wadding" and Otto a "patchy" hide, and Rosalie is *the softest thing in
 * every frame she occupies*, which is a comparative and not an exclusive. So this
 * is the same fabric, in an older dye (`SOW` in `palette.ts`).
 *
 * What differs is **proportion**, and it is doing the characterisation:
 *
 * | | Rosalie | her mother |
 * |---|---|---|
 * | body length ÷ height | ~1.4 (a ball) | ~3.1 (a barrel) |
 * | head ÷ body length | 0.37 | 0.21 |
 * | leg ÷ height | 0.33 | 0.42 |
 *
 * Roundness is what reads as *toy*, and D-002 makes that load-bearing for
 * Rosalie. An adult who has been in this building for years has had the round
 * pressed out of her. She is still soft; she is no longer a ball. Read the table
 * as the sentence "this is what you become if you stay", because that is the only
 * line of dialogue available in a wordless game.
 *
 * ## Why she is authored lying down
 *
 * A farrowing sow lies on her side almost permanently — it is how the piglets
 * reach her, and it is the posture the crate exists to enforce. She has exactly
 * one pose in this beat and there is no rig for her, so the pose is the mesh.
 *
 * She is nevertheless **built standing and then laid over**, by rotating the
 * whole assembly about Z and dropping it until it rests on the floor. Two reasons,
 * and the second is the real one:
 *
 *   1. it reuses Rosalie's construction language (one lofted torso whose radius is
 *      a continuous function of length — the G1 rebuild) instead of inventing a
 *      second way to build a pig;
 *   2. a lying body authored directly has its proportions expressed in a rotated
 *      frame, so **every future change to them has to be done in that frame too**.
 *      Standing is the frame these numbers mean something in.
 *
 * The drop is measured off the geometry rather than derived by hand. Hand-deriving
 * the resting height of a rotated lofted surface with four splayed limbs is
 * arithmetic that is wrong the first time and silently wrong after the next edit.
 *
 * ## The one thing that moves
 *
 * `breath`. It is the only life she has, it is the difference between a sleeping
 * animal and a prop, and it is a named pivot at floor level scaled in Y so she
 * swells upward without sinking. See `findSowParts`.
 */

import * as THREE from 'three';
import { SOW } from '@/render/palette';
import { surface } from './materials';
import type { AssetDefinition } from './types';

/**
 * Her real dimensions, standing, metres. **These are the argument.**
 *
 * A commercial sow is about 1.8 m long. Nothing here is shrunk to be convenient,
 * for exactly the reason `farrowing.ts` gives about the 2.9 m ceiling and the
 * 1.05 m door handle: the mismatch is the point and it costs nothing to keep.
 */
export const SOW_SCALE = { length: 1.72, width: 0.46, height: 0.78 } as const;

/** Hip line. Higher off the ground than Rosalie's, proportionally and absolutely. */
const HIP_Y = 0.33;

/**
 * The barrel. A cylinder that has been sat on, not a ball.
 *
 * `cy` is deliberately low relative to her height — a sow's belly hangs, and the
 * gap between belly and floor is most of what makes an adult read as heavy rather
 * than as large.
 */
const BODY = { x: 0.23, y: 0.225, z: 0.62, cy: 0.46, cz: -0.06 };

/**
 * Head. Small against the body, which is the adult proportion.
 *
 * `cz` is set so the BACK of the skull sits ~80 mm INSIDE the torso's neck
 * opening. The first pass had it 12 mm outside, and from the rear three-quarter
 * angle that read as a head balanced on a neck rather than joined to one — the
 * same defect G1 rejected on Rosalie, arrived at from the opposite direction.
 * The skull is wider than the neck where they meet, which is how a real one sits;
 * that only works if it is buried.
 */
const HEAD = { r: 0.185, cy: 0.5, cz: 0.6 };

/** Neck pivot, well inside the shoulder so the head swings from within her. */
const NECK = { y: 0.5, z: 0.44 };

/** Snout: longer and blunter than a piglet's. Age lengthens the face. */
const SNOUT = { rBase: 0.105, rTip: 0.086, length: 0.15, pitch: 0.1 };

/** Ears, sized against the skull as Rosalie's are. */
const EAR = { radius: 0.088, length: 0.2, flop: 0.86, splay: 0.34 };

/** Legs: longer and thinner in proportion than a piglet's, and set wide. */
const LEG = {
  radius: 0.052,
  hoofRadius: 0.058,
  frontSpreadX: 0.14,
  backSpreadX: 0.17,
  frontZ: 0.3,
  backZ: -0.46,
};

/** Teats. Two rows down her belly, and they are why the crate exists. */
const TEAT = { radius: 0.023, length: 0.032, rows: 6, spreadX: 0.09, fromZ: 0.28, toZ: -0.4 };

const SEG = { limb: 8 };

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Her torso, as ONE lofted surface — rump through barrel through shoulder to the
 * neck opening. Same construction as Rosalie's and for the same reason (two
 * ellipsoids meeting always leave a crease; a third bridging them reads as a
 * collar), with the profile functions doing the adult proportions.
 *
 * The differences from hers, all deliberate:
 *   - the barrel stays at nearly full width for far longer before tapering, which
 *     is what makes it a barrel;
 *   - the belly sags rather than flattening, because she is not a toy that has
 *     been sat on, she is a toy that has been full;
 *   - the spine dips behind the shoulder. A straight back reads as healthy.
 */
function buildTorsoGeometry(): THREE.BufferGeometry {
  const RINGS = 30;
  const SEGS = 26;
  const zBack = BODY.cz - BODY.z;
  const zFront = BODY.cz + BODY.z * 0.82;

  /** Rounds the rump so the loft closes rather than ending as a tube. */
  const capped = (u: number): number => {
    const uc = 0.16;
    if (u >= uc) return 1;
    const k = 1 - u / uc;
    return Math.sqrt(Math.max(0, 1 - k * k));
  };

  // Taper starts at 0.62 rather than Rosalie's 0.26. She is wide almost all the
  // way to the shoulder; that is the single strongest adult cue available in
  // profile, and it is the one that survives being seen through cage bars.
  const halfWidth = (u: number): number => BODY.x * capped(u) * (1 - 0.55 * smoothstep(0.62, 1, u));
  const halfHeight = (u: number): number => BODY.y * capped(u) * (1 - 0.32 * smoothstep(0.68, 1, u));
  // A dip behind the shoulder and a rise over it. On a young pig this line is
  // straight; on a worn-out one it is not, and the sag is legible from the side
  // at any distance where she is visible at all.
  const centreY = (u: number): number =>
    BODY.cy - 0.022 * Math.sin(smoothstep(0.15, 0.85, u) * Math.PI) + 0.03 * smoothstep(0.72, 1, u);

  const positions: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= RINGS; i++) {
    const u = i / RINGS;
    const hw = halfWidth(u);
    const hh = halfHeight(u);
    const cy = centreY(u);
    const z = zBack + (zFront - zBack) * u;
    for (let j = 0; j <= SEGS; j++) {
      const theta = (j / SEGS) * Math.PI * 2;
      const c = Math.cos(theta);
      // The belly HANGS. Rosalie's equivalent term flattens hers where a stuffed
      // toy settles; this one pushes it further down, and only under the barrel
      // where she carried a litter, tapering out toward both ends.
      const sagWindow = Math.sin(smoothstep(0.1, 0.9, u) * Math.PI);
      const sag = c < 0 ? 1 - 0.2 * c * sagWindow : 1;
      positions.push(hw * Math.sin(theta), cy + hh * c * sag, z);
    }
  }

  for (let i = 0; i < RINGS; i++) {
    for (let j = 0; j < SEGS; j++) {
      const a = i * (SEGS + 1) + j;
      const b = a + SEGS + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }

  // Cap the neck opening: it sits inside the skull, but she is seen from below
  // and from behind bars, and an open tube would show as a hole in her chest.
  const centreIndex = positions.length / 3;
  positions.push(0, centreY(1), zFront);
  const lastRing = RINGS * (SEGS + 1);
  for (let j = 0; j < SEGS; j++) {
    indices.push(centreIndex, lastRing + j + 1, lastRing + j);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Wedge the skull toward the snout, so the snout looks grown rather than glued on. */
function shapeHead(g: THREE.BufferGeometry): void {
  const pos = g.attributes['position'];
  if (!pos) return;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    // Harder taper than Rosalie's 0.36: an adult skull is a wedge, a piglet's is
    // a ball. This is the same lever her body length uses, on the head.
    const taper = 1 - 0.44 * Math.max(0, z) ** 1.1;
    const crown = y > 0 ? 1 - 0.1 * y : 1;
    pos.setXYZ(i, x * taper * HEAD.r * 0.92, y * crown * taper * HEAD.r * 0.9, z * HEAD.r * 1.3);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
}

function mesh(geo: THREE.BufferGeometry, mat: THREE.Material, name: string): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat);
  m.name = name;
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function pivot(name: string, x: number, y: number, z: number): THREE.Object3D {
  const o = new THREE.Object3D();
  o.name = name;
  o.position.set(x, y, z);
  return o;
}

/** One ear: a rounded teardrop hanging forward and out, cupped, never pointed. */
function buildEar(side: 1 | -1, fabric: THREE.Material): THREE.Object3D {
  const root = pivot(side > 0 ? 'ear.L' : 'ear.R', side * 0.078, 0.09, 0.06);
  const geo = new THREE.SphereGeometry(1, 12, 12);
  const pos = geo.attributes['position'];
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      const taper = 1 - 0.52 * Math.max(0, y);
      pos.setXYZ(
        i,
        pos.getX(i) * EAR.radius * taper,
        (y * 0.5 + 0.5) * EAR.length,
        pos.getZ(i) * EAR.radius * 0.36 * taper + 0.24 * EAR.radius * y * y,
      );
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }
  root.rotation.set(EAR.flop, 0, side * -EAR.splay);
  root.add(mesh(geo, fabric, 'ear'));
  return root;
}

/**
 * One leg, folded rather than straight.
 *
 * `fold` rotates it about the hip. A lying sow does not have four legs sticking
 * out sideways like a table on its side — the underside pair is tucked beneath
 * her and the upper pair falls forward. Folding at the hip is enough to read
 * that, and it costs no rig.
 */
function buildLeg(name: string, x: number, z: number, fold: number, fabric: THREE.Material): THREE.Object3D {
  const root = pivot(name, x, HIP_Y, z);
  const top = 0.09;
  const bottom = -HIP_Y + LEG.hoofRadius * 0.62;
  const length = top - bottom;
  const shaft = new THREE.CylinderGeometry(LEG.radius * 0.82, LEG.hoofRadius, length, SEG.limb, 1);
  shaft.translate(0, bottom + length / 2, 0);
  root.add(mesh(shaft, fabric, 'leg-shaft'));
  const hoof = new THREE.SphereGeometry(LEG.hoofRadius, SEG.limb, 8);
  hoof.scale(1, 0.62, 1);
  hoof.translate(0, bottom, 0);
  root.add(mesh(hoof, fabric, 'trotter'));
  root.rotation.x = fold;
  return root;
}

/**
 * Standing, in the frame her proportions are written in. Never added to a scene
 * directly — `build()` lays this over. Exported only for the arithmetic.
 */
function buildStanding(): THREE.Group {
  const standing = new THREE.Group();
  standing.name = 'sow-standing';

  const fabric = surface('fabric', SOW.fabric);
  const skin = surface('fabric', SOW.fabricShadow);
  const button = surface('fabric', SOW.button);

  standing.add(
    mesh(
      buildTorsoGeometry(),
      // Her seam runs down her spine as Rosalie's does, and it is drawn against
      // the torso's local X = 0 — which works here for the same reason it works
      // on Rosalie and nowhere else: the torso is one lofted surface, so its
      // local midline IS her spine. `seamColour` is what makes hers read as
      // strained rather than as new.
      surface('fabric', SOW.fabric, { seam: true, seamWidth: 0.011, seamColour: SOW.seam }),
      'body',
    ),
  );

  const head = pivot('head', 0, NECK.y, NECK.z);
  standing.add(head);

  const headGeo = new THREE.SphereGeometry(1, 20, 14);
  shapeHead(headGeo);
  headGeo.translate(0, HEAD.cy - NECK.y, HEAD.cz - NECK.z);
  head.add(mesh(headGeo, fabric, 'head-mesh'));

  // Button eyes, welded on and never animated. Same rule as Rosalie's (D-002):
  // they do not emote, the player projects. On her it matters more, not less —
  // a mother with a sad face would be the game telling you how to feel about the
  // single image the whole beat is built on.
  for (const side of [1, -1] as const) {
    const az = side * 0.52;
    const el = 0.4;
    const r = HEAD.r * 0.92;
    const eye = new THREE.SphereGeometry(0.024, 12, 8);
    eye.scale(1, 1, 0.55);
    const m = mesh(eye, button, side > 0 ? 'eye.L' : 'eye.R');
    m.position.set(
      Math.sin(az) * Math.cos(el) * r * 0.92,
      HEAD.cy - NECK.y + Math.sin(el) * r * 0.86,
      HEAD.cz - NECK.z + Math.cos(az) * Math.cos(el) * r * 1.16,
    );
    m.lookAt(m.position.clone().add(new THREE.Vector3(Math.sin(az) * 0.6, 0.2, 1)));
    head.add(m);
  }

  head.add(buildEar(1, fabric), buildEar(-1, fabric));

  const snout = pivot('snout', 0, HEAD.cy - NECK.y - 0.022, HEAD.cz - NECK.z + HEAD.r * 0.9);
  snout.rotation.x = -SNOUT.pitch;
  head.add(snout);

  const barrel = new THREE.CylinderGeometry(SNOUT.rTip, SNOUT.rBase, SNOUT.length, 14, 1);
  barrel.rotateX(Math.PI / 2);
  barrel.translate(0, 0, SNOUT.length / 2);
  snout.add(mesh(barrel, skin, 'snout'));

  const disc = new THREE.SphereGeometry(SNOUT.rTip, 14, 10);
  disc.scale(1, 1, 0.3);
  disc.translate(0, 0, SNOUT.length);
  snout.add(mesh(disc, skin, 'snout-disc'));

  for (const side of [1, -1] as const) {
    const nostril = new THREE.SphereGeometry(0.019, 8, 6);
    nostril.scale(0.75, 1.2, 0.6);
    nostril.translate(side * 0.036, -0.008, SNOUT.length + 0.016);
    snout.add(mesh(nostril, button, side > 0 ? 'nostril.L' : 'nostril.R'));
  }

  // Tail. Short, and NOT curled — a docked tail, which is what every pig in a
  // unit like this has. It is one of the few pieces of the world's cruelty that
  // can be stated on a body without depicting anything (spec §2's content
  // restraint), and it is the detail that separates this from a picture book.
  // Rosalie still has hers, and that is a fact about her age, not her luck.
  const tail = pivot('tail', 0, BODY.cy + 0.16, BODY.cz - BODY.z * 0.9);
  standing.add(tail);
  const stub = new THREE.CylinderGeometry(0.018, 0.026, 0.07, 8, 1);
  stub.translate(0, 0.035, 0);
  tail.add(mesh(stub, fabric, 'tail-stub'));
  tail.rotation.set(-0.5, 0, 0);

  // Teats, in two rows. They are not decoration: they are the reason she is in a
  // cage that stops her turning over, and the reason the creep areas exist either
  // side of her. The whole machine is built around this one part of her body.
  for (const side of [1, -1] as const) {
    for (let i = 0; i < TEAT.rows; i++) {
      const t = i / (TEAT.rows - 1);
      const z = TEAT.fromZ + (TEAT.toZ - TEAT.fromZ) * t;
      // A soft nub, not a cylinder.
      //
      // Built as a cylinder first, and from the rear three-quarter angle the top
      // row silhouetted against the background as a line of studs — a small form
      // with a hard end reads as hardware whatever it is attached to, and she is
      // the softest object in the room after Rosalie. A squashed sphere half
      // buried in her belly reads as part of the same toy.
      const teat = new THREE.SphereGeometry(TEAT.radius, 8, 6);
      teat.scale(1, TEAT.length / TEAT.radius, 1);
      teat.translate(0, -TEAT.length * 0.45, 0);
      // `fabric`, NOT `skin`. Painted in the darker shadow colour they read at a
      // distance as a row of dark studs down her flank rather than as part of
      // her — a small form in a contrasting value becomes a spike, whatever
      // shape it is. Her snout gets away with `skin` because it is large.
      const m = mesh(teat, fabric, `teat.${side > 0 ? 'L' : 'R'}.${i}`);
      // Placed on the sagging underside, splayed outward with the belly.
      m.position.set(side * TEAT.spreadX, BODY.cy - BODY.y * 1.06, z);
      m.rotation.z = side * 0.34;
      standing.add(m);
    }
  }

  // Front pair falls forward, back pair tucks under. Signs differ because they
  // fold toward each other, which is what a sow lying on her side actually does.
  standing.add(
    buildLeg('leg.FL', LEG.frontSpreadX, LEG.frontZ, -0.72, fabric),
    buildLeg('leg.FR', -LEG.frontSpreadX, LEG.frontZ, -0.46, fabric),
    buildLeg('leg.BL', LEG.backSpreadX, LEG.backZ, 0.66, fabric),
    buildLeg('leg.BR', -LEG.backSpreadX, LEG.backZ, 0.42, fabric),
  );

  return standing;
}

/**
 * Lying on her right side, resting on the floor, head toward +Z.
 *
 * Node order, and every level of it earns its keep:
 *
 *   sow          (asset origin, on the floor, centred under her)
 *     breath     scaled in Y only — see findSowParts
 *       lie      rotated onto her side and dropped to rest
 *         sow-standing
 *
 * `breath` sits ABOVE `lie`, at y = 0, and is unrotated. Both facts are
 * load-bearing:
 *
 *   - **At y = 0**, because a Y scale about the origin multiplies every world Y in
 *     the chain — so her lowest vertex, which rests at exactly 0, stays at exactly
 *     0 and only her top rises. A breath node at her middle would sink her into
 *     the concrete on every exhale, which is invisible in a still and reads as
 *     vibration in motion.
 *   - **Unrotated**, because that makes `lie.position` world-aligned, which is why
 *     the settle below can be written as "subtract the minimum Y" instead of
 *     needing the axis mapping of a −90° Z rotation worked out by hand. This
 *     project has shipped an inverted sign under a confident comment before
 *     (D-020's slope pitch); the remedy is to not do the arithmetic.
 *
 * One happy accident worth recording, because it would be easy to "fix": her
 * standing WIDTH axis becomes world Y once she is over, so scaling in Y expands
 * her back-to-belly. That is exactly where a ribcage expands. Breathing her along
 * world Y is anatomically right rather than merely convenient.
 */
function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'sow';

  const breath = pivot('breath', 0, 0, 0);
  group.add(breath);

  const lie = pivot('lie', 0, 0, 0);
  // Onto her side, belly and teats toward the asset's own **local −Z**.
  //
  // A rotation of +90 degrees about Z maps her standing (x, y) to (−y, x), so her
  // belly — which is at standing −Y — comes out at local +X; `bornBay.ts` then
  // yaws her by +90 degrees, which sends local +X to world −Z, and world −Z is
  // where the creep it places her litter in is.
  //
  // The sign was the other way round and the comment beside it asserted the
  // opposite of what the arithmetic did, so **she lay with her back to her own
  // litter** — the feeding side facing the empty creep across the crate. A sow
  // whose piglets cannot reach her is wrong in a way a player feels without being
  // able to name it. Found on independent review, which re-derived the axes
  // instead of reading the comment.
  lie.rotation.z = Math.PI / 2;
  breath.add(lie);

  const standing = buildStanding();
  lie.add(standing);

  // Settle her onto y = 0 and centre her in X/Z, measured rather than derived.
  //
  // The resting height of a rotated lofted surface with four folded limbs and two
  // rows of teats is arithmetic that is wrong the first time and silently wrong
  // after the next proportion change. `docs/asset-conventions.md` requires the
  // origin at ground contact and `asset.spec.ts` holds it to a millimetre, so
  // this is measured off the geometry that actually exists.
  //
  // Written onto `lie.position`, which is in `breath`'s unrotated frame and
  // therefore world-aligned. Writing it onto `standing.position` instead would put
  // it INSIDE the −90° rotation, where "up" is −X and every sign is a coin flip.
  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  lie.position.y -= bounds.min.y;
  // Her length runs along Z once she is over, and her head reaches further than
  // her rump, so an uncentred origin lands well forward of her middle — which
  // frames every acceptance shot off centre and puts the bay's placement
  // coordinates a hand's width from where they read on the page.
  lie.position.x -= (bounds.min.x + bounds.max.x) / 2;
  lie.position.z -= (bounds.min.z + bounds.max.z) / 2;

  return group;
}

/** The two nodes anything outside this file is allowed to touch. */
export interface SowParts {
  /**
   * Scale this in Y to make her breathe, and in Y ONLY.
   *
   * A sow's respiratory rate at rest is 15–20 a minute, which is slow enough that
   * a player notices the pause at the top of each one. That pause is the entire
   * performance — she has no face (D-002), so breathing is the only thing that
   * distinguishes her from a prop, and it is the same channel D-009 makes
   * Rosalie's stealth UI. Amplitude belongs at the call site; 1.5 % is plenty.
   */
  breath: THREE.Object3D;
  /**
   * Her head, on the neck pivot, in the STANDING frame — so its local X is up
   * once she is lying down. Rotate it a few degrees and no more.
   *
   * It exists for exactly one moment in beat 1: she is nudged, and she answers by
   * lifting her head as far as the bars allow, which is not far. Do not use it to
   * make her look at Rosalie fondly. Attention, never emotion (D-002 amendment) —
   * and here the attention is what the cage refuses.
   */
  head: THREE.Object3D;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`sow: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findSowParts(root: THREE.Object3D): SowParts {
  return { breath: required(root, 'breath'), head: required(root, 'head') };
}

export const sowAsset: AssetDefinition = {
  id: 'sow',
  label: 'Sow (her mother)',
  description:
    'An adult plush sow, lying on her side as a farrowing crate requires. 1.72 m long in a 0.65 m lane — she cannot turn around, and that is the whole of beat 1.',
  // Her LYING extents, which are her standing ones with two axes swapped: the
  // 0.78 m she stands tall is now across the lane, the 0.46 m she measures across
  // is now her height off the deck, and her length is untouched. Stating the
  // standing figures here would frame the preview for a pig that is not there.
  footprint: { width: SOW_SCALE.height, height: SOW_SCALE.width, length: SOW_SCALE.length },
  create: build,
};
